import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Button, Flex, Select } from '@mantine/core';
import { useMemo, useState } from 'react';
import type { UserOption } from './AssignSignerForm.types';

interface Props {
	options: UserOption[];
	isLoading?: boolean;
	isPending?: boolean;
	onConfirm: (userId: string) => void;
	onCancel: () => void;
	placeholder?: string;
	confirmLabel?: string;
	nothingFoundMessage?: string;
	selectTestId?: string;
	confirmTestId?: string;
	cancelTestId?: string;
}

export const AssignSignerForm = ({
	options,
	isLoading = false,
	isPending = false,
	onConfirm,
	onCancel,
	placeholder = 'Search for a user...',
	confirmLabel = 'Confirm',
	nothingFoundMessage = 'No users found',
	selectTestId,
	confirmTestId,
	cancelTestId,
}: Props) => {
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');

	const filteredOptions = useMemo(() => {
		if (!searchValue) {
			return options;
		}

		return options.filter(
			(option) =>
				option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
				option.email?.toLowerCase().includes(searchValue.toLowerCase())
		);
	}, [options, searchValue]);

	const handleConfirm = () => {
		if (!selectedUserId) {
			return;
		}

		onConfirm(selectedUserId);
	};

	return (
		<div
			style={{
				padding: '0.75rem',
				borderTop: `1px solid ${defaultTheme.v3.colors.gray[2]}`,
			}}
		>
			<Flex direction="column" gap="0.75rem">
				<Select
					data-testid={selectTestId}
					placeholder={placeholder}
					data={filteredOptions}
					value={selectedUserId}
					onChange={setSelectedUserId}
					searchable
					searchValue={searchValue}
					onSearchChange={setSearchValue}
					disabled={isLoading || isPending}
					nothingFoundMessage={nothingFoundMessage}
				/>
				<Flex gap="0.5rem" justify="flex-end">
					<Button
						data-testid={cancelTestId}
						variant="outline"
						onClick={onCancel}
						disabled={isPending}
					>
						Cancel
					</Button>
					<Button
						data-testid={confirmTestId}
						onClick={handleConfirm}
						disabled={!selectedUserId && !isPending}
						loading={isPending}
					>
						{confirmLabel}
					</Button>
				</Flex>
			</Flex>
		</div>
	);
};
